/**
 * useConnection — detects slow network conditions via the Network Information API
 * Returns `true` when the user is on a slow connection (2G/3G or saveData enabled).
 * Falls back gracefully in browsers that don't support navigator.connection.
 */

import { useState, useEffect } from "react";

type NetworkConnection = EventTarget & {
  effectiveType?: string;
  saveData?: boolean;
  addEventListener: (type: string, listener: () => void) => void;
  removeEventListener: (type: string, listener: () => void) => void;
};

function getIsSlowConnection(): boolean {
  try {
    const conn = (navigator as Navigator & { connection?: NetworkConnection }).connection;
    if (!conn) return false;
    const { effectiveType, saveData } = conn;
    return !!(saveData || effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g');
  } catch {
    return false;
  }
}

export function useConnection() {
  const [isSlow, setIsSlow] = useState<boolean>(() => {
    // Check the attribute set by main.tsx during boot (before React hydrates)
    if (typeof document !== 'undefined') {
      if (document.documentElement.getAttribute('data-slow-connection') === '1') return true;
    }
    return getIsSlowConnection();
  });

  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: NetworkConnection }).connection;
    if (!conn) return;

    const handleChange = () => {
      const slow = getIsSlowConnection();
      setIsSlow(slow);
      // Keep the data attribute in sync so other parts of the app can read it
      document.documentElement.setAttribute('data-slow-connection', slow ? '1' : '0');
    };

    conn.addEventListener('change', handleChange);
    return () => conn.removeEventListener('change', handleChange);
  }, []);

  return { isSlow };
}
